import {Quaternion, Vector3,} from 'three';
import type {StandardViewCameraFrame} from '../view-orientation/standardViewTypes';
import {
  ViewportGizmoControlAction,
  type ViewportGizmoControlAction as ViewportGizmoControlActionValue,
} from './ViewportGizmoControlAction';


/**
 * 将外部按钮或指针拖动转换为保持目标和距离的相机帧。
 *
 * 求解器不直接修改相机或控制器，并复用内部数学对象，避免拖动热路径频繁分配。
 */
export class ViewportGizmoCameraRotationSolver {
  /** 外部按钮单次旋转角度。 */
  public static readonly CONTROL_ANGLE = Math.PI / 4;

  /** 相机相对目标的偏移缓存。 */
  private readonly offset = new Vector3();

  /** 旋转后的相机朝上方向缓存。 */
  private readonly rotatedUp = new Vector3();

  /** 当前观察方向缓存。 */
  private readonly viewDirection = new Vector3();

  /** 当前相机局部右轴缓存。 */
  private readonly right = new Vector3();

  /** 输出相机位置缓存。 */
  private readonly outputPosition = new Vector3();

  /** 轴角旋转缓存。 */
  private readonly rotation = new Quaternion();

  /**
   * 求解外部按钮对应的相机帧。
   *
   * @param position 当前相机位置。
   * @param target 当前观察目标。
   * @param up 当前相机朝上方向。
   * @param action 六种有限外部控制动作之一。
   * @returns 保持目标与距离的新相机帧。
   * @throws {TypeError} 当运行时传入不受支持的导航动作时抛出错误。
   */
  public solveControlAction(
    position: Vector3,
    target: Vector3,
    up: Vector3,
    action: ViewportGizmoControlActionValue,
  ): StandardViewCameraFrame {
    const angle = ViewportGizmoCameraRotationSolver.CONTROL_ANGLE;

    switch (action) {
      case ViewportGizmoControlAction.OrbitUp:
        return this.solveOrbit(position, target, up, 0, -angle);
      case ViewportGizmoControlAction.OrbitDown:
        return this.solveOrbit(position, target, up, 0, angle);
      case ViewportGizmoControlAction.OrbitLeft:
        return this.solveOrbit(position, target, up, -angle, 0);
      case ViewportGizmoControlAction.OrbitRight:
        return this.solveOrbit(position, target, up, angle, 0);
      case ViewportGizmoControlAction.RollCounterClockwise:
        // 相机 up 与屏幕中模型的视觉滚转方向相反，逆时针视图需要负向相机滚转。
        return this.solveRoll(position, target, up, -angle);
      case ViewportGizmoControlAction.RollClockwise:
        // 顺时针视图需要正向相机滚转，确保视觉方向与按钮箭头一致。
        return this.solveRoll(position, target, up, angle);
    }

    // 运行时若外部绕过 TypeScript 传入非法动作，必须显式失败，避免返回未定义相机帧。
    throw new TypeError(`不支持的视口方向导航动作：${String(action)}`);
  }

  /**
   * 求解中央立方体拖动对应的增量环绕帧。
   *
   * @param position 当前相机位置。
   * @param target 当前观察目标。
   * @param up 当前相机朝上方向。
   * @param horizontalAngle 水平屏幕拖动弧度。
   * @param verticalAngle 垂直屏幕拖动弧度。
   * @returns 增量旋转后的相机帧。
   */
  public solvePointerOrbit(
    position: Vector3,
    target: Vector3,
    up: Vector3,
    horizontalAngle: number,
    verticalAngle: number,
  ): StandardViewCameraFrame {
    // 屏幕拖动方向与相机绕世界观察目标旋转方向相反。
    return this.solveOrbit(
      position,
      target,
      up,
      -horizontalAngle,
      -verticalAngle,
    );
  }

  /**
   * 围绕当前相机 up 和局部 right 依次执行环绕。
   *
   * @param position 当前相机位置。
   * @param target 当前观察目标。
   * @param up 当前相机朝上方向。
   * @param horizontalAngle 水平环绕角度。
   * @param verticalAngle 垂直环绕角度。
   * @returns 求解后的相机帧。
   */
  private solveOrbit(
    position: Vector3,
    target: Vector3,
    up: Vector3,
    horizontalAngle: number,
    verticalAngle: number,
  ): StandardViewCameraFrame {
    this.offset.copy(position).sub(target);
    this.rotatedUp.copy(up).normalize();

    if (Math.abs(horizontalAngle) > Number.EPSILON) {
      this.rotation.setFromAxisAngle(this.rotatedUp, horizontalAngle);
      this.offset.applyQuaternion(this.rotation);
    }

    if (Math.abs(verticalAngle) > Number.EPSILON) {
      this.viewDirection.copy(this.offset).normalize();
      this.right.copy(this.rotatedUp).cross(this.viewDirection).normalize();
      if (this.right.lengthSq() > Number.EPSILON) {
        this.rotation.setFromAxisAngle(this.right, verticalAngle);
        this.offset.applyQuaternion(this.rotation);
        this.rotatedUp.applyQuaternion(this.rotation);
      }
    }

    return this.createFrame(target, this.offset, this.rotatedUp);
  }

  /**
   * 保持位置不变，仅绕观察方向旋转相机 up。
   *
   * @param position 当前相机位置。
   * @param target 当前观察目标。
   * @param up 当前相机朝上方向。
   * @param angle 滚转弧度。
   * @returns 求解后的相机帧。
   */
  private solveRoll(
    position: Vector3,
    target: Vector3,
    up: Vector3,
    angle: number,
  ): StandardViewCameraFrame {
    this.offset.copy(position).sub(target);
    this.viewDirection.copy(this.offset).normalize();
    this.rotation.setFromAxisAngle(this.viewDirection, angle);
    this.rotatedUp.copy(up).normalize().applyQuaternion(this.rotation);
    return this.createFrame(target, this.offset, this.rotatedUp);
  }

  /**
   * 将 Three.js 向量转换为不可变相机帧元组。
   *
   * @param target 观察目标。
   * @param offset 相机相对目标的偏移。
   * @param up 相机朝上方向。
   * @returns plain data 相机帧。
   */
  private createFrame(
    target: Vector3,
    offset: Vector3,
    up: Vector3,
  ): StandardViewCameraFrame {
    this.outputPosition.copy(target).add(offset);
    return {
      position: [
        this.outputPosition.x,
        this.outputPosition.y,
        this.outputPosition.z,
      ],
      target: [target.x, target.y, target.z],
      up: [up.x, up.y, up.z],
    };
  }
}

